import './side-navigation.css';

import { DsIcon, DsTooltip } from '@descomplica/design-system-react';
import { useViewModel } from '@descomplica/microfrontend-react';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';

import { Button } from '@/presentation/shared-components/button/button';
import { RedirectContainer } from '@/presentation/shared-components/redirect';
import { Title } from '@/presentation/shared-components/text';

import { ClassroomViewModel, Lesson } from '../../classroom.vm';
import { AccordionContainer } from './accordion';
import CollapseButton from './collapse-button/collapse-button';
import HighlightCard from './highlight-card/highlight-card';
import LessonAccordionContainer from './lesson-accordion';
import LiveAccordionContainer from './live-accordion';

const SideNavigationContainer = () => {
  const vm = useViewModel(ClassroomViewModel);
  const [collapsed, setCollapsed] = useState<boolean>(false);
  if (!vm.classroom) {
    return null;
  }

  const accordionTitle = <Title>{vm.classroom.course.name}</Title>;
  const ariaLabelOpen = 'Mostrar descrição da aula';
  const ariaLabelClose = 'Ocultar descrição da aula';

  const showCollapseButton =
    !window.matchMedia('(min-width:768px)').matches ||
    window.matchMedia('(orientation: portrait)').matches ||
    window.matchMedia('(orientation: landscape) and (max-width: 1024px)').matches
      ? 'hidden'
      : '';

  const redirectUrl =
    vm.nextCoursesForClassroom !== '' ? `/classroom/course/${vm.nextCoursesForClassroom}` : false;

  const onHighlightCardClick = (lesson: Lesson) => {
    vm.setCurrentLesson(lesson);
    vm.toggleMobileSidebar();
    vm.handleLiveLessonPlayed();
  };

  return (
    <div
      className={`${
        collapsed ? 'collapse-container-side-navigation' : 'container-side-navigation'
      }`}
    >
      <nav
        className={`side-navigation ${collapsed ? 'collapse' : ''} ${
          vm.mobileSidebarOpen ? '' : 'hidden'
        }`}
      >
        <div className={`${collapsed ? 'collapse-content' : 'topic-lesson'}`}>
          <AccordionContainer
            accordionTitle={accordionTitle}
            ariaLabel={{ open: ariaLabelOpen, close: ariaLabelClose }}
          >
            <p className="topic-description">{vm.classroom.course.description}</p>
          </AccordionContainer>
        </div>

        {vm.currentLiveLesson ? (
          <HighlightCard
            status="Rolando Agora"
            title={vm.currentLiveLesson?.streaming.title}
            backgroundURL={vm.currentLesson?.thubmnail}
            subtitle={vm.currentLiveLesson?.teacher}
            onClick={() => onHighlightCardClick(vm.currentLiveLesson)}
          />
        ) : null}

        <ul className={`${collapsed ? 'collapse-content' : 'lesson-container'}`}>
          {vm.classroom.course.lessons?.map((lesson, index) => {
            if (lesson?.streaming && lesson.videos.length === 0) {
              return <LiveAccordionContainer key={index} live={lesson} />;
            }
            return <LessonAccordionContainer key={index} lesson={lesson} />;
          })}

          {vm.hasHighestClassifierGuid
            ? redirectUrl && <RedirectContainer href={redirectUrl} text={'Ir para próximo curso'} />
            : null}
        </ul>
        {!collapsed && (
          <ul className="certificate-button">
            {!vm.isCertificateEnabled ? (
              <DsTooltip
                className="tooltip"
                text={`Finalize pelo menos ${vm.certificateMinimalPercentage}% do conteúdo para baixar o seu certificado`}
                position="top"
                placement="center"
              >
                <Button
                  className="icon-button btn gray disabled"
                  disabled
                  ariaLabel="Baixar certificado"
                  onClick={() => {}}
                >
                  <div className="button-text">
                    <DsIcon className="locked-icon" image="locked" color="gray-dark" />
                    Certificado
                  </div>
                </Button>
              </DsTooltip>
            ) : (
              <Button
                className="icon-button btn green"
                ariaLabel="Baixar certificado"
                onClick={() =>
                  vm.setHasCertificateOpen(true, `/course/${vm.classroom.course.guid}`)
                }
              >
                <div className="button-text">Certificado</div>
              </Button>
            )}
          </ul>
        )}
      </nav>

      {!showCollapseButton && (
        <CollapseButton handleCollapse={setCollapsed} collapsed={collapsed} />
      )}
    </div>
  );
};

export default observer(SideNavigationContainer);
